import type { WebsiteSection } from 'src/api/generated';
import type { WebsiteLegalTexts } from '../types/website-draft.types';

const getSectionContent = (
  sections: WebsiteSection[] | undefined,
  type: string
): Record<string, unknown> => {
  const content = sections?.find((section) => section.type === type)?.content;

  return content && typeof content === 'object'
    ? (content as Record<string, unknown>)
    : {};
};

/**
 * Resolves the editable text for a structured website section.
 *
 * @param sections - Configured website sections.
 * @param type - Section type to resolve.
 * @param formatter - Legacy/canonical content formatter used without raw input.
 * @returns Raw editor input when present, otherwise formatted section content.
 */
export const getWebsiteSectionInputText = (
  sections: WebsiteSection[] | undefined,
  type: string,
  formatter: (content: Record<string, unknown>) => string
): string => {
  const content = getSectionContent(sections, type);
  const inputText = content.inputText;

  return typeof inputText === 'string' ? inputText : formatter(content);
};

const formatStringItems = (content: Record<string, unknown>): string => {
  const items = content.items;

  return Array.isArray(items)
    ? items
        .filter((item): item is string => typeof item === 'string')
        .join('\n')
    : '';
};

const readStringValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/**
 * Resolves both legal page textareas without normalizing in-progress input.
 *
 * @param sections - Configured website sections.
 * @returns The raw legal notice and privacy text, empty when absent.
 */
export const getWebsiteLegalInputTexts = (
  sections: WebsiteSection[] | undefined
): WebsiteLegalTexts => {
  const content = getSectionContent(sections, 'legal');

  return {
    text:
      readStringValue(content.inputText) ?? readStringValue(content.text) ?? '',
    privacyText: readStringValue(content.privacyText) ?? '',
  };
};

/**
 * Builds the `legal` section content templates read: the notice as `text`
 * (with its raw `inputText`) and, when written, `privacyText`.
 *
 * @param texts - Raw legal notice and privacy text.
 * @returns Section content for the legal page.
 */
export const buildWebsiteLegalSectionContent = (
  texts: WebsiteLegalTexts
): Record<string, unknown> => ({
  inputText: texts.text,
  text: texts.text,
  ...(texts.privacyText ? { privacyText: texts.privacyText } : {}),
});

/**
 * Whether the legal page has any text to show, which keeps its section on.
 *
 * @param texts - Raw legal notice and privacy text.
 * @returns Whether either text has visible characters.
 */
export const hasWebsiteLegalText = (texts: WebsiteLegalTexts): boolean =>
  Boolean(texts.text.trim() || texts.privacyText.trim());

/**
 * Resolves the Services textarea without normalizing in-progress user input.
 *
 * @param sections - Configured website sections.
 * @param legacyServices - Legacy top-level service strings.
 * @returns Raw Services editor input or a backwards-compatible fallback.
 */
export const getWebsiteServicesInputText = (
  sections: WebsiteSection[] | undefined,
  legacyServices: string[] | undefined
): string =>
  getWebsiteSectionInputText(sections, 'services', (content) =>
    legacyServices?.length
      ? legacyServices.join('\n')
      : formatStringItems(content)
  );
